import { auth } from '../../firebase';
import { getApiUrl, readJsonResponse } from '../../utils/apiConfig';
import type {
  StoreSyncDetail,
  StoreSyncFrequency,
  StoreSyncRow,
  StoreSyncSummary,
  StoreSyncTile,
} from './model';
import type { SyncRunKind } from '../../utils/syncRunKind';

export interface StoreSyncListParams {
  scope: 'linked' | 'unlinked';
  status?: StoreSyncTile;
  q?: string;
  platform?: string;
  schedule?: StoreSyncFrequency;
  kind?: SyncRunKind;
  sort: 'attention' | 'name' | 'updated';
  page: number;
  pageSize: number;
  refresh?: boolean;
}

const BASE = '/admin/integrations/store-sync';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${getApiUrl()}${BASE}${path}`, {
    ...init,
    headers: { ...(init?.headers || {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const body = await readJsonResponse<{ success?: boolean; error?: string } & T>(res, 'Unexpected response from the server.');
  if (!res.ok || body.success === false) throw new Error(body.error || `Request failed (HTTP ${res.status})`);
  return body;
}

export const fetchStoreSyncSummary = (refresh = false) =>
  call<StoreSyncSummary>(`/summary${refresh ? '?refresh=true' : ''}`);

export function fetchStoreSyncRows(params: StoreSyncListParams) {
  const qs = new URLSearchParams();
  qs.set('scope', params.scope);
  qs.set('sort', params.sort);
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.status) qs.set('status', params.status);
  if (params.q?.trim()) qs.set('q', params.q.trim());
  if (params.platform) qs.set('platform', params.platform);
  if (params.schedule) qs.set('schedule', params.schedule);
  if (params.kind) qs.set('kind', params.kind);
  if (params.refresh) qs.set('refresh', 'true');
  return call<{ rows: StoreSyncRow[]; total: number; page: number; pageSize: number }>(`/integrations?${qs}`);
}

export const fetchStoreSyncDetail = (id: string) => call<StoreSyncDetail>(`/integrations/${encodeURIComponent(id)}`);

export const setStoreSyncPaused = (id: string, paused: boolean) =>
  call<{ paused: boolean }>(`/integrations/${encodeURIComponent(id)}/schedule`, {
    method: 'POST',
    body: JSON.stringify({ paused }),
  });

export interface StoreSyncRunResult {
  syncRunId: string | null;
  status: string;
  existing: boolean;
  platform: string;
  /** Lightspeed and Lightspeed eCom: the background job, polled through the detail's `adminRun`. */
  adminRunId?: string | null;
  /**
   * `queued`: off the request (Shopify on the sync queue, Lightspeed on the admin run worker).
   * `inline`: only the local emulator with no Pub/Sub topic, where a Lightspeed run happens in the request.
   */
  mode: 'queued' | 'inline';
  /** Inline runs that stopped at their time budget; the next run continues where this one stopped. */
  continues?: boolean;
  productsProcessed?: number;
  errorCount?: number;
}

export const runStoreSync = (id: string, kind: 'pricing' | 'compare' | 'full') =>
  call<StoreSyncRunResult>(`/integrations/${encodeURIComponent(id)}/run`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
